import { type Control, Controller } from 'react-hook-form';

import { useFieldWiring } from '@nesso/ui/hooks/use-field-wiring';
import { Field, FieldError, FieldLabel } from '@nesso/ui/primitives/field';
import { Input } from '@nesso/ui/primitives/input';

import { formatTimeOfDay } from '../../../lib/format-time-of-day';
import { parseTimeOfDay } from '../../../lib/parse-time-of-day';
import type {
    ScheduleSessionFormOutput,
    ScheduleSessionFormValues,
} from '../../../model/schedule-session-form.schema';

type Props = {
    control: Control<ScheduleSessionFormValues, unknown, ScheduleSessionFormOutput>;
    onStartTimeChange: (value: string) => void;
};

// Typed text, not a time control — nothing in the clinician has a time picker. Each reformats on
// blur; unparseable text is left for the schema to refuse on submit.
export function ScheduleSessionTimeFields({ control, onStartTimeChange }: Props) {
    return (
        <div className="flex items-start gap-2">
            <Controller
                control={control}
                name="startTime"
                render={({ field, fieldState }) => (
                    <ScheduleSessionTimeField
                        id="schedule-session-start-time"
                        label="Start"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={() => {
                            field.onBlur();
                            const minutes = parseTimeOfDay(field.value);
                            if (minutes !== null) onStartTimeChange(formatTimeOfDay(minutes));
                        }}
                        error={fieldState.error?.message}
                    />
                )}
            />
            <span
                aria-hidden="true"
                className="flex h-10 w-4 shrink-0 items-center justify-center text-foreground"
            >
                –
            </span>
            <Controller
                control={control}
                name="endTime"
                render={({ field, fieldState }) => (
                    <ScheduleSessionTimeField
                        id="schedule-session-end-time"
                        label="End"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={() => {
                            field.onBlur();
                            const minutes = parseTimeOfDay(field.value);
                            if (minutes !== null) field.onChange(formatTimeOfDay(minutes));
                        }}
                        error={fieldState.error?.message}
                    />
                )}
            />
        </div>
    );
}

type TimeFieldProps = {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    error?: string;
};

// 93/95px fields collapse to a single shared `w-24` on the 4px spacing scale. The accessible name
// stays via a sr-only `FieldLabel`, matching "Start"/"End" where these fields are queried by label.
function ScheduleSessionTimeField({ id, label, value, onChange, onBlur, error }: TimeFieldProps) {
    const wiring = useFieldWiring({ id, error });

    return (
        <Field data-invalid={wiring.dataInvalid} className="w-24">
            <FieldLabel htmlFor={wiring.controlId} className="sr-only">
                {label}
            </FieldLabel>
            <Input
                {...wiring.controlProps}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
            />
            {/* Not FieldSlot: the message runs past the 96px field instead of wrapping. */}
            {wiring.errorMessage && (
                <FieldError id={wiring.errorId} className="whitespace-nowrap">
                    {wiring.errorMessage}
                </FieldError>
            )}
        </Field>
    );
}
